import * as React from "react";
import { useOrganization } from "@clerk/react";

export interface UnitMemberDirectoryEntry {
  imageUrl?: string;
  name: string;
  userId: string;
}

/**
 * page_editors only stores a Clerk user_id (text) — display name and avatar
 * live in Clerk, not Postgres, so the page-access picker resolves them from
 * the current unit's Clerk membership list rather than a local profiles
 * table (this platform has none; Clerk is the single source of identity).
 * Ported from scoutingops-chat's use-unit-member-directory.ts, the
 * platform's established pattern for this exact need.
 */
export function useUnitMemberDirectory(): {
  directory: Map<string, UnitMemberDirectoryEntry>;
  isLoading: boolean;
} {
  const { memberships, isLoaded } = useOrganization({
    memberships: { pageSize: 100 },
  });

  const directory = React.useMemo(() => {
    const map = new Map<string, UnitMemberDirectoryEntry>();
    for (const membership of memberships?.data ?? []) {
      const userId = membership.publicUserData?.userId;
      if (!userId) continue;
      const name =
        [membership.publicUserData?.firstName, membership.publicUserData?.lastName].filter(Boolean).join(" ") ||
        membership.publicUserData?.identifier ||
        userId;
      map.set(userId, { imageUrl: membership.publicUserData?.imageUrl, name, userId });
    }
    return map;
  }, [memberships?.data]);

  return { directory, isLoading: !isLoaded };
}
